import React, { useEffect, useState, useMemo } from 'react';
import { Map as MapboxMap, Source, Layer } from '@vis.gl/react-mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, Typography, Card, CardContent, useTheme, Select, MenuItem, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useVehicleContext } from '../../../context/vehicleContext';
import { Vehicle } from '../../../models/vehicle';

const EuropeMap: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { vehicles: contextVehicles, isLoading } = useVehicleContext();
  const [dateSelection, setDateSelection] = useState<'today' | 'tomorrow'>('today');
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [heatmapData, setHeatmapData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const filtered = contextVehicles.filter((vehicle) => {
      if (!vehicle.emptyAt) return false;
      const emptyDate = new Date(vehicle.emptyAt);
      const emptyDateOnly = new Date(emptyDate.getFullYear(), emptyDate.getMonth(), emptyDate.getDate());

      if (dateSelection === 'today') {
        return emptyDateOnly.getTime() === todayOnly.getTime();
      } else {
        const tomorrowOnly = new Date(todayOnly);
        tomorrowOnly.setDate(tomorrowOnly.getDate() + 1);
        return emptyDateOnly.getTime() === tomorrowOnly.getTime();
      }
    });

    setFilteredVehicles(filtered);
  }, [contextVehicles, dateSelection]);

  useEffect(() => {
    if (!filteredVehicles.length) {
      setHeatmapData(null);
      return;
    }

    const locationMap = new globalThis.Map<string, { coordinates: [number, number]; count: number }>();

    filteredVehicles.forEach((vehicle) => {
      const emptyIn = vehicle.emptyIn;
      if (!emptyIn?.geoCoordinate) return;

      const lng = emptyIn.geoCoordinate.longitude;
      const lat = emptyIn.geoCoordinate.latitude;

      if (lng === undefined || lat === undefined || lng === null || lat === null ||
        isNaN(lng) || isNaN(lat) || lng < -180 || lng > 180 || lat < -90 || lat > 90 ||
        (lng === 0 && lat === 0)) {
        return;
      }

      const roundedLng = Math.round(lng * 100) / 100;
      const roundedLat = Math.round(lat * 100) / 100;
      const key = `${roundedLng},${roundedLat}`;

      if (locationMap.has(key)) {
        const existing = locationMap.get(key);
        if (existing) {
          existing.count += 1;
        }
      } else {
        locationMap.set(key, { coordinates: [lng, lat], count: 1 });
      }
    });

    const features: GeoJSON.Feature[] = Array.from(locationMap.values()).map((location: { coordinates: [number, number]; count: number }) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: location.coordinates,
      },
      properties: {
        intensity: location.count,
      },
    }));

    setHeatmapData({
      type: 'FeatureCollection',
      features,
    });
  }, [filteredVehicles]);

  const mapStyle = useMemo(() => theme.palette.mode === 'dark'
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/streets-v12', [theme.palette.mode]);

  return (
    <Card
      sx={{
        width: { xs: '100%', md: '60%' },
        flex: { md: '0 0 60%' },
        height: { xs: 'auto', md: '100%' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        borderRadius: { xs: 2, sm: 3 },
        border: (theme: any) => `1px solid ${theme.palette.border.default}`,
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{
        p: 0, m: 0, flex: 1, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 },
        '&.MuiCardContent-root': {
          backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 0.75, sm: 1, md: 1.25 }, py: { xs: 0.5, sm: 0.75, md: 1 }, flexShrink: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.125rem' },
              color: 'text.dark',
            }}
          >
            {t('Heatmap of empty fleet positions')}
          </Typography>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={dateSelection}
              onChange={(e) => setDateSelection(e.target.value as 'today' | 'tomorrow')}
              sx={{
                backgroundColor: 'background.inputGrey',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  py: 1,
                  fontSize: '0.875rem',
                  color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.purple,
                },
              }}
            >
              <MenuItem value="today">{t('Today')}</MenuItem>
              <MenuItem value="tomorrow">{t('Tomorrow')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 300, sm: 350, md: 0 },
            borderRadius: 0,
            overflow: 'hidden',
            position: 'relative',
            margin: 0,
            '& .mapboxgl-ctrl-attrib': {
              display: 'none',
            },
            '& .mapboxgl-ctrl-logo': {
              display: 'none',
            },
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.backdrop',
                zIndex: 1000,
                borderRadius: 1,
              }}
            >
              <Typography sx={{ color: 'text.white' }}>{t('Loading...')}</Typography>
            </Box>
          )}

          <MapboxMap
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || ''}
            mapStyle={mapStyle}
            initialViewState={{
              longitude: 8.0,
              latitude: 50.0,
              zoom: 5,
            }}
            minZoom={3}
            maxZoom={10}
            onLoad={() => setMapLoaded(true)}
            style={{ width: '100%', height: '100%', margin: 0, padding: 0 }}
            reuseMaps={false}
          >
            {heatmapData && mapLoaded && (
              <Source id="unloading-heatmap" type="geojson" data={heatmapData}>
                <Layer
                  id="unloading-heatmap-layer"
                  type="circle"
                  paint={{
                    'circle-radius': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      0, 6,
                      5, 8,
                      9, 10,
                    ],
                    'circle-color': 'rgba(139, 92, 246, 0.6)',
                    'circle-stroke-width': 2,
                    'circle-stroke-color': 'rgba(139, 92, 246, 1)',
                    'circle-opacity': 0.9,
                  }}
                />
              </Source>
            )}
          </MapboxMap>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EuropeMap;

